// Shift Handover (nursing-ops sprint, Epic 9 / D11). The outgoing nurse
// creates a handover for a shift — the backend auto-populates one SBAR row
// per admitted patient on the ward — edits each patient's SBAR, then submits
// it; the incoming nurse acknowledges it. Strict state machine: draft ->
// submitted -> acknowledged (main/ipd/manage_shift_handover.py). Every
// mutation refetches the list so the UI always reflects the real backend
// state, and a state-guard rejection (e.g. submitting a non-draft handover)
// surfaces as a toast rather than a false success.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { shiftLabel } from "../shiftWindow";
import { useShiftSchedule } from "../hooks";
import { useNurseData } from "../NurseDataContext";
import { getShiftHandovers, setShiftHandoverStatus, listOf } from "../api/nurseApi";
import useBedCensusData from "../../ipd/hooks/useBedCensusData";
import ShiftHandoverCreateDialog from "../components/ShiftHandoverCreateDialog";
import ShiftHandoverPatientCard from "../components/ShiftHandoverPatientCard";

const STATUS_LABEL = { draft: "Draft", submitted: "Submitted", acknowledged: "Acknowledged" };
const STATUS_COLOR = { draft: "default", submitted: "warning", acknowledged: "success" };
const STATUS_FILTER_OPTIONS = ["All", "draft", "submitted", "acknowledged"];

export default function ShiftHandover() {
  const { schedule } = useShiftSchedule();
  const { currentNurseId } = useNurseData();
  const { wards } = useBedCensusData();

  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionSaving, setActionSaving] = useState(false);

  const [shiftDate, setShiftDate] = useState("");
  const [outgoingShift, setOutgoingShift] = useState("All");
  const [status, setStatus] = useState("All");

  const reload = useCallback(() => {
    setLoading(true);
    const params = {};
    if (shiftDate) params.shift_date = shiftDate;
    if (outgoingShift !== "All") params.outgoing_shift = outgoingShift;
    if (status !== "All") params.status = status;
    return getShiftHandovers(params)
      .then((res) => {
        if (res?.status !== "Success") {
          toast.error(res?.message || "Failed to load shift handovers");
          setHandovers([]);
          return;
        }
        setHandovers(listOf(res));
      })
      .catch(() => {
        toast.error("Failed to load shift handovers");
        setHandovers([]);
      })
      .finally(() => setLoading(false));
  }, [shiftDate, outgoingShift, status]);

  useEffect(() => { reload(); }, [reload]);

  const selected = useMemo(
    () => handovers.find((h) => String(h.id) === String(selectedId)) || null,
    [handovers, selectedId]
  );

  const handleCreated = (newId) => {
    setCreateOpen(false);
    setSelectedId(newId);
    reload();
  };

  const runAction = async (action) => {
    if (!selected) return;
    setActionSaving(true);
    try {
      const res = await setShiftHandoverStatus({ id: selected.id, action, actor: currentNurseId });
      if (res?.status !== "Success") {
        toast.error(res?.message || `Failed to ${action} handover`);
        return;
      }
      toast.success(res?.message || `Handover ${action === "submit" ? "submitted" : "acknowledged"}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${action} handover`);
    } finally {
      setActionSaving(false);
    }
  };

  const sortedRows = useMemo(
    () => [...handovers].sort((a, b) => String(b.shift_date).localeCompare(String(a.shift_date)) || (b.id - a.id)),
    [handovers]
  );

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6">Shift Handover</Typography>
          <Typography variant="body2" color="text.secondary">
            Hand off patient care with a per-patient SBAR summary.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Handover
        </Button>
      </Stack>

      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            type="date" size="small" label="Shift date" value={shiftDate}
            onChange={(e) => setShiftDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }}
          />
          <TextField
            select size="small" label="Outgoing shift" value={outgoingShift}
            onChange={(e) => setOutgoingShift(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="All">All</MenuItem>
            {schedule.map((s) => (
              <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Status" value={status}
            onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}
          >
            {STATUS_FILTER_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s === "All" ? "All" : STATUS_LABEL[s]}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto", mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Date</TableCell>
              <TableCell>Ward</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Handed over by</TableCell>
              <TableCell>Patients</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((h) => (
              <TableRow key={h.id} hover selected={String(h.id) === String(selectedId)}>
                <TableCell>{h.shift_date}</TableCell>
                <TableCell>{h.ward_id || h.department_id || "—"}</TableCell>
                <TableCell>{shiftLabel(h.outgoing_shift, schedule)} → {shiftLabel(h.incoming_shift, schedule)}</TableCell>
                <TableCell>{h.handed_over_by_name || h.handed_over_by}</TableCell>
                <TableCell>{(h.patients || []).length}</TableCell>
                <TableCell><Chip size="small" label={STATUS_LABEL[h.status] || h.status} color={STATUS_COLOR[h.status] || "default"} /></TableCell>
                <TableCell align="right">
                  <Button size="small" variant={String(h.id) === String(selectedId) ? "contained" : "outlined"} onClick={() => setSelectedId(h.id)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && sortedRows.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No shift handovers match the filter.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {selected && (
        <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="subtitle1">
                {selected.shift_date} — {shiftLabel(selected.outgoing_shift, schedule)} → {shiftLabel(selected.incoming_shift, schedule)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.ward_id || selected.department_id || "—"} · Handed over by{" "}
                {selected.handed_over_by_name || selected.handed_over_by}
                {selected.received_by ? ` · Acknowledged by ${selected.received_by_name || selected.received_by}` : ""}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Chip label={STATUS_LABEL[selected.status] || selected.status} color={STATUS_COLOR[selected.status] || "default"} />
              {selected.status === "draft" && (
                <Button size="small" variant="contained" disabled={actionSaving} onClick={() => runAction("submit")}>
                  Submit
                </Button>
              )}
              {selected.status === "submitted" && (
                <Button size="small" variant="contained" color="success" disabled={actionSaving} onClick={() => runAction("acknowledge")}>
                  Acknowledge
                </Button>
              )}
            </Stack>
          </Stack>

          {selected.status !== "draft" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This handover is {STATUS_LABEL[selected.status] || selected.status} — SBAR entries are read-only.
            </Alert>
          )}

          <Stack spacing={1.5}>
            {(selected.patients || []).map((p) => (
              <ShiftHandoverPatientCard
                key={p.id}
                patient={p}
                editable={selected.status === "draft"}
                onSaved={reload}
              />
            ))}
            {(selected.patients || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">No patients on this handover.</Typography>
            )}
          </Stack>
        </Paper>
      )}

      <ShiftHandoverCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        wards={wards}
        handedOverBy={currentNurseId}
        onCreated={handleCreated}
      />
    </Box>
  );
}
